import { createApi } from "@reduxjs/toolkit/query/react"

import { ApiTypes, UITypes } from "types"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"

export const contractorsApi = createApi({
  reducerPath: "contractors",
  tagTypes: ["contractors", "contractor-users", "devicesMessages"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getContractors: builder.query<ApiTypes.Res.Contractors, UITypes.Params>({
      query: arg => {
        return {
          url: `/contractors`,
          method: "GET",
          params: prepareQueryParams(arg),
        }
      },
    }),
    getContractor: builder.query<{ data: ApiTypes.Model.Contractor }, string>({
      query: id => {
        return {
          url: `/contractors/contractor/${id}`,
          method: "GET",
        }
      },
    }),
    getContractorUsers: builder.query<ApiTypes.Res.Users, UITypes.Params>({
      query: arg => {
        return {
          url: `/contractor-users`,
          params: prepareQueryParams(arg),
        }
      },
      providesTags: ["contractor-users"],
    }),
    getStatuses: builder.query<ApiTypes.Res.Status[], void>({
      query: () => ({
        url: `/contractors/statuses`,
      }),
    }),
    createContractor: builder.query<void, FormData>({
      query: body => ({
        url: `/contractors`,
        method: "POST",
        body,
        headers: { Accept: "application/json" },
      }),
    }),
    createContractorUser: builder.mutation<void, ApiTypes.Req.CreateContractorUser>({
      query: body => ({
        url: `/contractor-users`,
        method: "POST",
        body,
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["contractor-users"],
    }),
    editContractorUser: builder.mutation<void, { body: ApiTypes.Req.EditContractorUser; id: number }>({
      query: data => ({
        url: `/contractor-users/${data.id}`,
        method: "PUT",
        body: data.body,
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["contractor-users"],
    }),
    editContractor: builder.query<void, { data: FormData; id: number }>({
      query: body => {
        return {
          url: `/contractors/${body.id}`,
          method: "POST",
          body: body.data,
          headers: {
            Accept: "application/json",
          },
        }
      },
    }),
    createMessageTemplate: builder.query<void, ApiTypes.Req.CreateMessageTemplate>({
      query: body => ({
        url: `/contractors/message-template-v2`,
        method: "POST",
        body: {
          ...body,
          version: 1,
          content_type: "text/markdown",
        },
      }),
    }),
    getMessageTemplates: builder.query<ApiTypes.Res.MessageTemplates, void>({
      query: () => ({
        url: `/contractors/message-template-v2`,
      }),
    }),
    getMessageTemplate: builder.query<{ data: ApiTypes.Model.MessageTemplate }, number>({
      query: id => ({
        url: `/contractors/message-template-v2/${id}`,
      }),
    }),
    deleteMessageTemplate: builder.query<void, string>({
      query: id => ({
        url: `/contractors/message-template-v2/${id}`,
        method: "DELETE",
      }),
    }),
    editMessageTemplate: builder.query<void, { body: ApiTypes.Req.CreateMessageTemplate; id: string }>({
      query: data => ({
        url: `/contractors/message-template-v2/${data.id}`,
        method: "PUT",
        body: {
          ...data.body,
          content_type: "text/markdown",
        },
      }),
    }),
    getContractorMessagesBatch: builder.query<ApiTypes.Res.MessageBatch, UITypes.Params>({
      query: arg => ({
        url: `/contractors/device-messages/batches`,
        params: prepareQueryParams(arg),
      }),
    }),
    getMessageReport: builder.query<ApiTypes.Res.MessageReports, { id: string; params: UITypes.Params }>({
      query: data => ({
        url: `/contractors/device-messages/batches/${data.id}`,
        params: prepareQueryParams(data.params),
        headers: { Accept: "application/json" },
      }),
    }),
    scheduleMessageForDevices: builder.mutation<void, ApiTypes.Req.ScheduleMessageForDevices>({
      query: data => ({
        url: `/contractors/device-messages/batches`,
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-type": "application/json",
        },
      }),
      invalidatesTags: ["devicesMessages"],
    }),
    cancelNextDelivery: builder.mutation<void, { id: number; status: string }>({
      query: data => ({
        url: `/contractors/device-messages/${data.id}/cancel-next-delivery`,
        method: "PUT",
        body: JSON.stringify({ status: data.status }),
        headers: {
          "Content-type": "application/json",
        },
      }),
      invalidatesTags: ["devicesMessages"],
    }),
    updateDeviceMessageStatus: builder.mutation<void, { id: number; status: string }>({
      query: data => ({
        url: `/contractors/device-messages/${data.id}/status`,
        method: "PUT",
        body: JSON.stringify({ status: data.status }),
        headers: {
          "Content-type": "application/json",
        },
      }),
      invalidatesTags: ["devicesMessages"],
    }),
    createMessageTemplateOld: builder.query<void, ApiTypes.Req.CreateMessageTemplateOld>({
      query: body => ({
        url: `/contractors/message-template`,
        method: "POST",
        body,
      }),
    }),
    getMessageTemplatesOld: builder.query<ApiTypes.Res.MessageTemplatesOld, void>({
      query: () => ({
        url: `/contractors/message-template`,
      }),
    }),
    getMessageTemplateOld: builder.query<{ data: ApiTypes.Model.MessageTemplateOld }, number>({
      query: id => ({
        url: `/contractors/message-template/${id}`,
      }),
    }),
    deleteMessageTemplateOld: builder.query<void, string>({
      query: id => ({
        url: `/contractors/message-template/${id}`,
        method: "DELETE",
      }),
    }),
    editMessageTemplateOld: builder.query<void, { body: ApiTypes.Req.CreateMessageTemplateOld; id: string }>({
      query: data => ({
        url: `/contractors/message-template/${data.id}`,
        method: "PUT",
        body: data.body,
      }),
    }),
    getMessageHistoryOld: builder.query<ApiTypes.Res.MessageHitories, UITypes.Params>({
      query: arg => ({
        url: `/contractors/messages-history`,
        params: prepareQueryParams(arg),
      }),
    }),
    generateMessagesOld: builder.query<void, ApiTypes.Req.GenerateMessages>({
      query: data => ({
        url: `/contractors/generate-messages`,
        method: "POST",
        body: JSON.stringify(data.body),
        params: prepareQueryParams(data.params),
        headers: {
          "Content-type": "application/json",
        },
      }),
    }),
    changeContractorPassword: builder.query<void, ApiTypes.Req.ChangeContractorPassword>({
      query: data => ({
        url: `/contractors/change-password`,
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-type": "application/json",
        },
      }),
    }),
    changeContractorProfile: builder.query<void, FormData>({
      query: data => ({
        url: `/contractors/profile`,
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      }),
    }),
    getContractorProfile: builder.query<{ data: ApiTypes.Res.ContractorProfile }, void>({
      query: () => ({
        url: `/contractors/profile`,
      }),
    }),
    putContractorsDevicesAsMissing: builder.query<void, UITypes.Params>({
      query: arg => {
        return {
          url: `/contractors/actions/mark-as-missing`,
          method: "PUT",
          params: prepareQueryParams(arg),
          headers: { Accept: "application/json" },
        }
      },
    }),
    putContractorsDevicesAsTrasfered: builder.query<void, UITypes.Params>({
      query: arg => {
        return {
          url: `/contractors/actions/transfer`,
          method: "PUT",
          params: prepareQueryParams(arg),
          headers: { Accept: "application/json" },
        }
      },
    }),
    changeContractorsDevicesStatus: builder.query<void, UITypes.Params>({
      query: params => {
        return {
          url: `/devices/update-phisical-status`,
          method: "POST",
          body: params,
          headers: { Accept: "application/json" },
        }
      },
    }),
    markDeviceAsWarranty: builder.query<void, string>({
      query: sn => {
        return {
          url: `/devices/mark-warranty`,
          method: "POST",
          params: prepareQueryParams({ sn }),
          headers: { Accept: "application/json" },
        }
      },
    }),
    checkServiceTitan: builder.query<void, UITypes.Params>({
      query: body => {
        return {
          url: `/contractors/service-titan/checking`,
          method: "POST",
          body,
          headers: { Accept: "application/json" },
        }
      },
    }),
    getSTMemberships: builder.query<ApiTypes.Res.VendorSTMemberships, UITypes.Params>({
      query: body => {
        return {
          method: "POST",
          url: `/contractors/service-titan/memberships`,
          body,
          headers: { Accept: "application/json" },
        }
      },
    }),
    getTemplateLibraryGroups: builder.query<{ data: ApiTypes.Model.TemplatesLibrary[] }, void>({
      query: () => ({
        url: `/contractors/template-library-groups`,
      }),
    }),
    addTemplateLibrary: builder.query<{ data: ApiTypes.Model.TemplatesLibrary[] }, number>({
      query: id => ({
        url: `/contractors/add-template-from-library/${id}`,
        method: "POST",
        headers: { Accept: "application/json" },
      }),
    }),
    getContractorUniversityUrl: builder.query<{ data: string }, void>({
      query: () => ({
        url: `/contractors/university-link`,
        headers: { Accept: "application/json" },
      }),
    }),
    getMessageReportOld: builder.query<ApiTypes.Res.MessageReportsOld, { id: string; params: UITypes.Params }>({
      query: data => ({
        url: `/contractors/messages-history/${data.id}/messages`,
        params: prepareQueryParams(data.params),
        headers: { Accept: "application/json" },
      }),
    }),
    getDevicesMessages: builder.query<ApiTypes.Res.DevicesMessages, number[]>({
      query: device_ids => {
        const searchParams = new URLSearchParams()
        device_ids.forEach(id => {
          searchParams.append("device_ids", String(id))
        })
        return {
          url: `/contractors/device-messages/devices?${searchParams.toString()}`,
          headers: { Accept: "application/json" },
        }
      },
      providesTags: ["devicesMessages"],
    }),
    getSameParentCompanies: builder.query<{ data: { id: string; name: string }[] }, void>({
      query: () => ({
        url: `/contractors/same-parent-companies`,
        headers: { Accept: "application/json" },
      }),
    }),
  }),
})

export const {
  useLazyGetContractorsQuery,
  useLazyGetContractorQuery,
  useLazyGetStatusesQuery,
  useLazyCreateContractorQuery,
  useCreateContractorUserMutation,
  useEditContractorUserMutation,
  useLazyEditContractorQuery,
  useLazyCreateMessageTemplateQuery,
  useLazyGetMessageTemplatesQuery,
  useLazyGetMessageTemplateQuery,
  useLazyDeleteMessageTemplateQuery,
  useLazyEditMessageTemplateQuery,
  useLazyGetContractorMessagesBatchQuery,
  useLazyGetMessageReportQuery,
  useScheduleMessageForDevicesMutation,
  useCancelNextDeliveryMutation,
  useUpdateDeviceMessageStatusMutation,
  useLazyCreateMessageTemplateOldQuery,
  useLazyGetMessageTemplatesOldQuery,
  useLazyGetMessageTemplateOldQuery,
  useLazyDeleteMessageTemplateOldQuery,
  useLazyEditMessageTemplateOldQuery,
  useLazyGetMessageHistoryOldQuery,
  useLazyGenerateMessagesOldQuery,
  useLazyGetMessageReportOldQuery,
  useLazyGetDevicesMessagesQuery,
  useLazyChangeContractorPasswordQuery,
  useLazyChangeContractorProfileQuery,
  useLazyGetContractorProfileQuery,
  useLazyPutContractorsDevicesAsMissingQuery,
  useLazyPutContractorsDevicesAsTrasferedQuery,
  useLazyChangeContractorsDevicesStatusQuery,
  useLazyMarkDeviceAsWarrantyQuery,
  useLazyCheckServiceTitanQuery,
  useLazyGetSTMembershipsQuery,
  useLazyGetTemplateLibraryGroupsQuery,
  useLazyAddTemplateLibraryQuery,
  useLazyGetContractorUniversityUrlQuery,
  useLazyGetSameParentCompaniesQuery,
  useLazyGetContractorUsersQuery,
} = contractorsApi

export const getContractors = contractorsApi.endpoints.getContractors
export const getStatuses = contractorsApi.endpoints.getStatuses
export const getMessageTemplates = contractorsApi.endpoints.getMessageTemplates
export const getMessageTemplatesOld = contractorsApi.endpoints.getMessageTemplatesOld
export const getTemplateLibrary = contractorsApi.endpoints.getTemplateLibraryGroups
export const getContractorProfile = contractorsApi.endpoints.getContractorProfile
